// Which Redcare programme a campaign belongs to — Search SPA or cross-sell —
// read off the campaign NAME, because nothing else carries it (#574).
//
// NOT exported through `@nova-ai/shared`'s main barrel — import it from
// `@nova-ai/shared/campaign-family`. Every addition to that barrel lands in the
// candidate projection's bundle and moves `CANDIDATE_PRODUCER_SOURCE_HASH`, and a
// moved producer identity BLOCKS activation of every candidate generation already
// built (`product-values/activation.ts`). An ads-only classifier has no business
// making the product-value pipeline rebuild, so it stays on its own subpath.
// `generate-candidate-producer-hash.test.ts` is the gate that catches a slip.
//
// WHY THE NAME. `marketplace_ad_reports_daily.campaign_type` looks like the
// right column and is not: it stores the export's `Success Metric Type`, which
// is `cpc` on every row in both programmes. Redcare's export has no programme
// column at all, so the name is the only signal, and this file is its single
// owner. Before this existed the rule lived in a CSS comment in one hand-built
// HTML report — which is how the dashboard came to show the same product twice
// with no way to tell the rows apart.
//
// THE NAME SHAPE, as exported:
//
//   (SOLD_OUT_)?<tenant>_<Programme>_<brand> (Marketplace Seller)_<product>
//
// for example
//
//   it_MarketplaceBooster_GymBeam (Marketplace Seller)_GymBeam Omega 3 …
//   SOLD_OUT_com_CrossSellReRank_GymBeam (Marketplace Seller)_GymBeam Vegan …
//
// so the programme is neither first nor at a fixed index: `SOLD_OUT_` moves it
// and contributes underscores of its own. We therefore match a whole
// UNDERSCORE-SEPARATED SEGMENT, which also keeps the product name — always last,
// free text — from classifying a row by accident.
//
// The programmes read completely differently and that is the point of splitting
// them: on Shop Apotheke IT the entire ad loss sits in Search SPA (96 % of spend
// at 112 % ROAS) while cross-sell earns at 731 % on the remaining 4 %. Any
// figure that pools them describes neither.

/** The two Redcare programmes. Constant-first so call sites reference a checked
 *  member instead of retyping the string literal. */
export const CAMPAIGN_FAMILY = {
  /** `MarketplaceBooster` — the keyword-driven Search SPA placements. */
  searchSpa: "search-spa",
  /** `CrossSellReRank` — placements on other products' detail pages. */
  crossSell: "cross-sell",
} as const

export type CampaignFamily = (typeof CAMPAIGN_FAMILY)[keyof typeof CAMPAIGN_FAMILY]

/** The same two members as a tuple, for `z.enum`. Derived from the object above
 *  so a wire schema can never list a family the classifier does not know. */
export const CAMPAIGN_FAMILIES = [CAMPAIGN_FAMILY.searchSpa, CAMPAIGN_FAMILY.crossSell] as const

/** Name segment → programme, lower-cased for a case-insensitive segment match.
 *  Redcare has re-cased its labels before; a re-casing must not unclassify the
 *  whole account. */
const FAMILY_BY_SEGMENT = new Map<string, CampaignFamily>([
  ["marketplacebooster", CAMPAIGN_FAMILY.searchSpa],
  ["crosssellrerank", CAMPAIGN_FAMILY.crossSell],
])

/**
 * The programme a campaign name names, or `null` when the name does not say.
 *
 * `null` is a real answer and callers must handle it rather than defaulting to a
 * family: a new or renamed Redcare programme would otherwise be quietly filed
 * under Search SPA, and a two-way filter would drop it off the page entirely.
 * The dashboard gives these rows an "Other" tab of their own, shown only when
 * some row needs it — so the tab appearing IS the alarm that Redcare changed its
 * naming, and no row is ever filtered into nowhere.
 *
 * A name that somehow claims BOTH programmes is `null` too. Guessing which half
 * of a contradiction to trust is worse than saying the name is unreadable.
 */
export function classifyCampaignFamily(
  campaignName: string | null | undefined
): CampaignFamily | null {
  if (!campaignName) {
    return null
  }
  const found = new Set<CampaignFamily>()
  for (const segment of campaignName.split("_")) {
    const family = FAMILY_BY_SEGMENT.get(segment.trim().toLowerCase())
    if (family) {
      found.add(family)
    }
  }
  const [only] = found
  return found.size === 1 && only ? only : null
}

/** How a programme is written for a human — in the UI, in the CLI report, and in
 *  anything the weekly review agent reads, so all three say it the same way. */
export function campaignFamilyLabel(family: CampaignFamily | null): string {
  if (family === CAMPAIGN_FAMILY.searchSpa) {
    return "Search-SPA"
  }
  if (family === CAMPAIGN_FAMILY.crossSell) {
    return "Cross-sell"
  }
  return "Other"
}
